import React from 'react';
import { CheckIcon, RefreshCwIcon } from 'lucide-react';
import { recruitments, type PipelineStep, type Recruitment } from '../lib/data';

// Guide V2 : le rouge marque l'étape en cours du pipeline. Aucune
// couleur dédiée à l'apprentissage — il se distingue par la surface encre.
function stepClasses(step: PipelineStep) {
  if (step.state === 'completed') return 'border-ink bg-ink text-white';
  if (step.state === 'current') return 'border-brand bg-brand text-white';
  return 'border-[#d8d5cf] bg-white text-transparent';
}
function labelClasses(step: PipelineStep) {
  if (step.state === 'completed') return 'text-ink';
  if (step.state === 'current') return 'text-brand font-500';
  return 'text-muted';
}
function Pipeline({
  steps
}: {
  steps: Recruitment['steps'];
}) {
  return <div className="flex items-center gap-1" data-id="element-14" data-mp="lmbymwu4lcn4">
      {steps.map((step, index) => {
      const nextStep = steps[index + 1];
      const connectorClass = nextStep?.state === 'completed' ? 'bg-ink' : nextStep?.state === 'current' ? 'bg-brand' : 'bg-line';
      return <div key={step.label} className="flex items-center gap-1" data-id="element-15" data-mp="4dfzgc94hcqn" data-mpi={index}>
            <div className="flex flex-col items-center gap-1" data-id="element-16" data-mp="c4iev4bdzu2">
              <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${stepClasses(step)}`} data-id="element-17" data-mp="a9ur91nrtma">
                {step.state === 'completed' && <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} data-id="element-18" data-mp="s2sgtzelsr41" />}
                {step.state === 'current' && step.label === 'Apprendre' && <RefreshCwIcon className="h-2.5 w-2.5" strokeWidth={2.5} data-id="element-19" data-mp="o0dxzfaxprcr" />}
              </span>
              <span className={`whitespace-nowrap text-[9.5px] leading-none ${labelClasses(step)}`} data-id="element-20" data-mp="e414oijaxfij">
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && <span className={`mb-4 h-px w-3.5 ${connectorClass}`} data-id="element-21" data-mp="2yu9prf0am19f" />}
          </div>;
    })}
    </div>;
}
function toneClasses(tone: Recruitment['nextActionTone']) {
  switch (tone) {
    case 'accent':
      return 'border-transparent bg-brand-soft text-brand';
    case 'attention':
      return 'border-attention/60 bg-attention-soft text-attention-text';
    // Apprentissage : surface encre. Moment fort, pas nouvelle teinte.
    case 'learning':
      return 'border-transparent bg-ink text-white';
    default:
      return 'border-line bg-canvas text-muted';
  }
}
function Row({
  item
}: {
  item: Recruitment;
}) {
  return <div className="flex flex-col gap-4 py-5 lg:flex-row lg:items-center lg:justify-between lg:gap-6" data-id="element-22" data-mp="2mcphu76xzehg">
      <div className="min-w-0 lg:w-60 lg:flex-none" data-id="element-23" data-mp="28laq9byyy4ys">
        <h3 className="text-[15px] font-500 text-ink" data-id="element-24" data-mp="2clujw5kc2qg6">{item.title}</h3>
        <p className="mt-0.5 text-[13px] text-muted" data-id="element-25" data-mp="2cm49vt5doxlv">
          {item.department} · {item.location}
        </p>
        <p className="mt-0.5 text-[12px] text-muted" data-id="element-26" data-mp="1yo67lm4c0rmq">Gestionnaire : {item.hiringManager}</p>
      </div>

      <div className="lg:flex-1 lg:px-2" data-id="element-27" data-mp="23n0sctlv0q9h">
        <Pipeline steps={item.steps} data-id="element-28" data-mp="1jyl53hjdkzl1" />
      </div>

      <div className="flex flex-wrap items-center gap-3 lg:w-64 lg:flex-none lg:justify-end" data-id="element-29" data-mp="1k1h3f6z4xqa0">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-consent-soft px-2.5 py-1 text-[12.5px] text-consent-text" data-id="element-30" data-mp="1f2e8ifcvd0fr">
          <span className="h-1.5 w-1.5 rounded-full bg-consent" data-id="element-31" data-mp="1oxy0iqobbez8" />
          {item.consented} candidats consentis
        </span>
        <span className={`rounded-full border px-3 py-1 text-[12px] font-500 ${toneClasses(item.nextActionTone)}`} data-id="element-32" data-mp="22x77czqmowqj">
          {item.nextAction}
        </span>
      </div>
    </div>;
}
export function ActiveRecruitments() {
  return <section className="mx-auto w-full max-w-6xl px-5 py-7 sm:px-8" data-id="element-33" data-mp="2cq4ph4rpkjom">
      <div className="flex items-baseline justify-between" data-id="element-34" data-mp="27q9zr4jo6055">
        <h2 className="font-serif text-2xl font-500 tracking-[-0.02em] text-ink" data-id="element-35" data-mp="2lognvsytglrm">Recrutements actifs</h2>
        <button type="button" className="rounded-card px-2 py-1 text-[13px] text-muted transition-colors hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas" data-id="element-36" data-mp="2hnoff1xrqkwe">
          Tout voir
        </button>
      </div>

      <div className="mt-3 divide-y divide-line border-t border-line" data-id="element-37" data-mp="or2mdluue5lq">
        {recruitments.map(item => <Row key={item.id} item={item} data-id="element-38" data-mp="trgoyodocncd" />)}
      </div>
    </section>;
}